// Order.js
// Data object for generating pdf reports
import pdfMake from 'pdfmake/build/pdfmake';
import vfsFonts from 'pdfmake/build/vfs_fonts'
import OrderData from './Order';
import ConsolidatedOrderData from './ConsolidatedOrder';
import dateFormat from 'dateformat';

// Class Definition
class Report {
  constructor(clients, products, orders) {
    this.clients = clients;
    this.products = products;
    this.orders = orders;
  }

  test = () => {
    this.getMultiClientReport();
  }

  getBuyReport = (order) => {
    if (!order) 
      order = this.orders.current;

    var docDefinition = {
      styles: this.getStyles(),
      content: [
        this.buildHeader("Resumo de Compra"),
        this.buildBuyTable(order),
      ],
    };
    
    return this.createPdf(docDefinition);
  }

  getPeriodBuyReport = (startOrder, endOrder, allOrders) => {
    var startDate = startOrder && startOrder.dateClosed ? new Date(startOrder.dateClosed) : new Date();
    var endDate = endOrder && endOrder.dateClosed ? new Date(endOrder.dateClosed) : new Date();
    // Ensure endDate covers the entire day
    endDate.setHours(23, 59, 59, 999);
    // Ensure startDate starts at the beginning of the day (just in case they are selected identically)
    startDate.setHours(0, 0, 0, 0);

    var validOrders = [];
    allOrders.archive.forEach(order => {
      if (!order.dateClosed) return;
      var date = new Date(order.dateClosed);
      if (date >= startDate && date <= endDate) {
        validOrders.push(order);
      }
    });

    // Check if current order is also within bounds (usually it's the newest)
    var currentDate = allOrders.current && allOrders.current.dateClosed ? new Date(allOrders.current.dateClosed) : new Date();
    if (currentDate >= startDate && currentDate <= endDate) {
      validOrders.push(allOrders.current);
    }


    // Group by day (using yyyy-mm-dd format for sorting)
    var ordersByDay = {};
    validOrders.forEach(order => {
      var date = new Date(order.dateClosed);
      var dayStr = dateFormat(date, "yyyy-mm-dd");
      if (!ordersByDay[dayStr]) {
        ordersByDay[dayStr] = [];
      }
      ordersByDay[dayStr].push(order);
    });

    var days = Object.keys(ordersByDay).sort().reverse();

    var content = [];

    // Day-by-day pages
    days.forEach((dayStr, index) => {
      var dayOrders = ordersByDay[dayStr];
      // Merge multiple orders on the same day into a single object structurally similar to a single order
      var mergedDayOrder = { dateClosed: dayOrders[0].dateClosed, byClient: {} };
      dayOrders.forEach(order => {
        Object.keys(order.byClient).forEach(clientId => {
          // We simplify by combining them even if the same client has multiple orders (unlikely but possible)
          if (!mergedDayOrder.byClient[clientId]) {
            mergedDayOrder.byClient[clientId] = order.byClient[clientId];
          } else {
            // If client has multiple orders on same day, we need to merge the products
            var existing = mergedDayOrder.byClient[clientId];
            var incoming = order.byClient[clientId];
            Object.keys(incoming).forEach(prodId => {
              if (!existing[prodId]) existing[prodId] = incoming[prodId];
              else {
                // Clone to avoid mutating original state
                existing[prodId] = { ...existing[prodId], quantity: existing[prodId].quantity + incoming[prodId].quantity };
              }
            });
          }
        });
      });

      content.push(this.buildHeader("Resumo de Compra"));
      content.push(this.buildBuyTable(mergedDayOrder));
      content.push({ text: '', pageBreak: 'after' });
    });

    // Final totalization page
    content.push(this.buildHeader("Total do Período"));
    content.push(this.buildPeriodTotalTable(validOrders));

    var docDefinition = {
      styles: this.getStyles(),
      content: content,
    };

    return this.createPdf(docDefinition);
  }

  getOrderReport = (clientId, orders, delivery) => {
    var client = this.clients.byId[clientId];
    if (!orders) orders = [ this.orders.current ];
    if (!client || orders.length === 0) return;

    var validOrders = 0;
    var docDefinition = {
      styles: this.getStyles(),
      content: [
        this.buildHeader("Nota de Compra"),
        this.buildClientHeader(client.name),
        orders.map((order) => {
          var clientOrder = order.byClient[clientId];
          if (!clientOrder)
            return null;

          validOrders++;
          return [
            this.buildDateHeader([order], delivery),
            this.buildOrderTable(clientOrder),
          ];
        }),
        validOrders > 1 ? this.buildClientTotalTable(orders, clientId) : null,
      ],
    };
    
    return this.createPdf(docDefinition);
  }

  getPriceReport = () => {
    if (!this.products || this.products.ids.length === 0) return;

    var productIds = this.products.ids.slice();
    productIds.sort((a, b) => {
      return ('' + this.products.byId[a].name).localeCompare(this.products.byId[b].name);
    });
    
    var body = [];
    var index = 0;

    productIds.forEach(id => {
      var product = this.products.byId[id];
      var unitStr = "";
      var priceStr = "";

      product.price.forEach((price) => {
        if (unitStr !== "") {
          unitStr += "\n";
          priceStr += "\n";
        }
        unitStr += price.unit;
        priceStr += 'R$ '+price.price.toFixed(2);
      });

      if (index % 2 === 0)
        body.push([]);
      index += 1;
    
      var target_array = body[body.length-1];
      target_array.push({text: product.name, style: 'tableProduct', verticalAlignment: 'center'})
      target_array.push({text: unitStr, alignment: 'center'})
      target_array.push({text: priceStr, alignment: 'center'})
    });

    if (index % 2 === 1)
      body[body.length-1].push({text: "", colSpan: 3})

    var mainTable = [
      {
        layout: 'orderLayout',
        style: 'table',
        table: {
          widths: ['*', 70, 70, '*', 70, 70],
          body: body,
        }
      },
    ];

    var docDefinition = {
      styles: this.getStyles(),
      content: [
        this.buildHeader("Tel. (21) 96410-1604\n     (21) 99756-6318"),
        this.buildPriceHeader(),
        mainTable,
      ],
    };
    
    return this.createPdf(docDefinition);
  }

  getConsolidatedOrderReport = (clientId, orders) => {
    var client = this.clients.byId[clientId];
    var clientOrders = [];

    for (var i = 0; i < orders.length; i++) {
      if (orders[i].byClient[clientId])
        clientOrders.push(orders[i].byClient[clientId])
    }

    var docDefinition = {
      styles: this.getStyles(),
      content: [
        this.buildHeader("Nota de Compra Consolidada"),
        this.buildClientHeader(client.name),
        [
          this.buildDateHeader(clientOrders),
          this.buildConsolidatedOrdersTable(clientOrders),
        ],
      ],
    };
    
    return this.createPdf(docDefinition);
  }
  
  getAllOrdersReport = (order, delivery) => {
    if (!order) order = this.orders.current;

    var clientIds = [];
    for (var clientId in order.byClient) {
      if (this.clients.byId[clientId])
        clientIds.push(clientId);
    }
    clientIds.sort((a, b) => {
      return ('' + this.clients.byId[a].name).localeCompare(this.clients.byId[b].name);
    })

    var docDefinition = {
      styles: this.getStyles(),
      content: [
        clientIds.map((id, i) => {
          return [
            this.buildHeader("Resumo de Compra"),
            this.buildClientHeader(this.clients.byId[id].name),
            this.buildDateHeader([order], delivery),
            this.buildOrderTable(order.byClient[id]),
            i < clientIds.length - 1 ? {text: '', pageBreak: 'after'} : null,
          ];
        }),
      ],
    };
    
    return this.createPdf(docDefinition);
  }

  getMultiClientReport = (order, delivery) => {
    if (!order) order = this.orders.current;

    var clientIds = [];
    for (var clientId in order.byClient) {
      if (this.clients.byId[clientId])
        clientIds.push(clientId);
    }
    clientIds.sort((a, b) => {
      return ('' + this.clients.byId[a].name).localeCompare(this.clients.byId[b].name);
    })

    var docDefinition = {
      styles: this.getStyles(),
      content: [
        this.buildHeader("Resumo de Pedidos"),
        this.buildDateHeader([order], delivery),
        clientIds.map((id) => {
          return [
            this.buildClientHeader(this.clients.byId[id].name),
            this.buildOrderTable(order.byClient[id]),
          ];
        }),
        this.buildOrderTotalTable(order),
      ],
    };
    
    return this.createPdf(docDefinition);
  }

  buildHeader = (docName) => {
    return {
      style: 'table',
      layout: 'noBorders',
      table: {
        widths: ['*', '*'],
        body: [
          [
            {text: 'Carlinhos', style: 'header'},
            {text: docName, alignment: 'right'},
          ],
        ],
      },
    }
  }

  buildClientHeader = (client) => {
    return {
      style: 'tableClient',
      table: {
        widths: [50, '*'],
        body: [
          [
            {text: 'Cliente', style: 'tableHeader'},
            {text: client, style: 'tableSubHeader' },
          ],
        ],
      },
    }
  }

  buildPriceHeader = () => {
    var dateStr = dateFormat(new Date(), "dd/mm/yyyy HH:ss");
    return {
      style: 'tableClient',
      table: {
        widths: ['*', 150],
        body: [
          [
            {text: 'Tabela de Preços', style: 'tableHeader'},
            {text: dateStr, style: 'tableSubHeader', alignment: 'center'},
          ],
        ],
      },
    }
  }

  buildDateHeader = (orders, deliver) => {
    var dateStr = "";
    var format = "dd/mm/yyyy";

    if (!orders || orders.length === 0)
      return;

    var dates = []
    for (var order in orders)
      dates.push(order.dateClosed ? new Date(order.dateClosed) : new Date());
    dates.sort();

    if (dates[0].getTime() === dates[dates.length - 1].getTime())
      dateStr = '';
    else
      dateStr = 'Compras entre ' + dateFormat(dates[0], format) + ' e ' + dateFormat(dates[dates.length-1], format)

    return [
      {
        layout: 'noBorders',
        style: 'table',
        table: {
          widths: ['*','*'],
          body: [
            [
              {text: dateStr, alignment: 'left'},
              {text: deliver ? 'Entrega em '+deliver : "", alignment: 'right'},
            ],
          ],
        }
      },
    ];
  }

  buildOrderTable = (order) => {
    var orderData = new OrderData(this.products, order);
    var productIds = this.products.ids.slice();
    productIds.sort((a, b) => {
      return ('' + this.products.byId[a].name).localeCompare(this.products.byId[b].name);
    });

    var body = [ this.buildOrderTableHeader() ];

    productIds.forEach(id => {
      if (!order[id]) return;
      var product = this.products.byId[id];
      var quantity = orderData.getQuantity(id);
      var total = orderData.getProductTotal(id);
      var pricePerUnit = Math.round(100*(total / quantity))/100;
      body.push(
        [
          product.name,
          {text: quantity, alignment: 'center'},
          {text: orderData.getUnit(id), alignment: 'center'},
          {text: 'R$ '+pricePerUnit.toFixed(2), alignment: 'center'},
          {text: 'R$ '+total.toFixed(2), alignment: 'center'},
        ]
      );
    });

    var layout = 'orderLayout'
    var adjust_value = orderData.getAdjustmentValue();
    if (adjust_value !== 0) {
      layout = 'orderLayoutWithSubtotal';
      body.push(this.buildOrderTableSubtotalLine(orderData.getSubtotal()));
      body.push(this.buildOrderTableAdjustmentLine(adjust_value, orderData.getAdjustmentDescription()));
    }
    body.push(this.buildOrderTableTotalsLine(orderData.getTotal()));

    return [
      {
        layout: layout,
        style: 'table',
        table: {
          widths: ['*', 70, 70, 70, 100],
          body: body,
        }
      },
    ];
  }

  buildConsolidatedOrdersTable = (orders) => {
    var orderData = new ConsolidatedOrderData(this.products, orders);
    var productIds = this.products.ids.slice();
    productIds.sort((a, b) => {
      return ('' + this.products.byId[a].name).localeCompare(this.products.byId[b].name);
    });

    var body = [ this.buildOrderTableHeader() ];
    productIds.forEach(id => {
      if (!orderData.hasProduct(id)) return;
      var product = this.products.byId[id];
      var units = orderData.getUnits(id);

      for (var u = 0; u < units.length; u++) {
        var quantity = orderData.getQuantity(id, units[u]);
        var total = orderData.getProductTotal(id, units[u]);
        var pricePerUnit = Math.round(100*(total / quantity))/100;
        body.push(
          [
            u === 0 ? product.name : "",
            {text: quantity, alignment: 'center'},
            {text: units[u], alignment: 'center'},
            {text: 'R$ '+pricePerUnit.toFixed(2), alignment: 'center'},
            {text: 'R$ '+total.toFixed(2), alignment: 'center'},
          ]
        );
      }
    });

    var layout = 'orderLayout';
    var adjustments = orderData.getAdjustmentList();
    if (adjustments.length > 0) {
      body.push(this.buildOrderTableSubtotalLine(orderData.getSubtotal()));
      adjustments.forEach(adj => {
        var dateStr = dateFormat(adj.date, "dd/mm/yyyy");
        var desc = dateStr;
        if (adj.description)
          desc = desc + ": " + adj.description;
        body.push(this.buildOrderTableAdjustmentLine(adj.value, desc));
      });
    }
    body.push(this.buildOrderTableTotalsLine(orderData.getTotal()));

    return [
      {
        layout: layout,
        style: 'table',
        table: {
          widths: ['*', 70, 70, 70, 100],
          body: body,
        }
      },
    ];
  }

  buildOrderTableHeader = () => {
    return [
      {text: 'Produto', style: 'tableHeader'},
      {text: 'Qtd', style: 'tableHeader', alignment: 'center'},
      {text: 'Unidade', style: 'tableHeader', alignment: 'center'},
      {text: 'Preço Unit.', style: 'tableHeader', alignment: 'center'},
      {text: 'Preço Total', style: 'tableHeader', alignment: 'center'}
    ];
  };

  buildOrderTableSubtotalLine = (subtotal) => {
    return [
      {text: 'Subtotal', style: 'tableTotal', colSpan: 4, alignment: 'right'},
      {},
      {},
      {},
      {text: 'R$ '+subtotal.toFixed(2), alignment: 'center'},
    ];
  };

  buildOrderTableAdjustmentLine = (value, desc) => {
    if (!value || value === 0)
      return null;

    var value_str = value < 0 ? '(R$ ' + (value * -1).toFixed(2) + ')' : 'R$ ' + value.toFixed(2);

    if (!desc || desc === "") {
      return [
        {text: value < 0 ? 'Desconto' : 'Ajuste de Compras Anteriores', style: 'tableTotal', colSpan: 4, alignment: 'right'},
        {},
        {},
        {},
        {text: value_str, alignment: 'center'},
      ];
    }
    else {
      return [
        {text: desc, colSpan: 3, alignment: 'left'},
        {},
        {},
        {text: value < 0 ? 'Desconto' : 'Ajuste', style: 'tableTotal', alignment: 'right'},
        {text: value_str, alignment: 'center'},
      ];
    }
  };

  buildOrderTableTotalsLine = (total) => {
    return [
      {text: 'Total', style: 'tableTotal', colSpan: 4, alignment: 'right'},
      {},
      {},
      {},
      {text: 'R$ '+total.toFixed(2), alignment: 'center'},
    ];
  };

  buildOrderTotalTable = (order) => {
    var total = 0;
    for (var clientId in order.byClient) {
      var orderData = new OrderData(this.products, order.byClient[clientId]);
      total += orderData.getTotal();
    }

    return this.buildTotalTable(total);
  };
  
  buildClientTotalTable = (orders, clientId) => {
    var total = 0;
    for (var i in orders) {
      var orderData = new OrderData(this.products, orders[i].byClient[clientId]);
      total += orderData.getTotal();
    }

    return this.buildTotalTable(total);
  };

  buildTotalTable = (total) => {
    return {
      style: 'table',
      table: {
        widths: ['*', '20%'],
        body: [
          [
            {text: 'Total dos Pedidos', alignment: 'right', style: 'tableHeader'},
            {text: 'R$ ' + total.toFixed(2), alignment: 'center', style: 'tableSubHeader' },
          ],
        ],
      },
    }
  };

  buildBuyTable = (order) => {
    var date = order.dateClosed ? new Date(order.dateClosed) : new Date();
    var dateStr = dateFormat(date, "dd/mm/yyyy");
    var productIds = this.products.ids.slice();
    productIds.sort((a, b) => {
      return ('' + this.products.byId[a].name).localeCompare(this.products.byId[b].name);
    });

    var body = [
      [
        {text: 'Produto', style: 'tableHeader'},
        {text: 'Qtd', style: 'tableHeader', alignment: 'center'},
        {text: 'Unidade', style: 'tableHeader', alignment: 'center'},
      ],
    ];

    productIds.forEach(id => {
      var qtd = {};
      for (var clientId in order.byClient) {
        var productState = order.byClient[clientId][id];
        if (!productState) continue;

        var unitId = productState.unitId
        if (!qtd[unitId])
          qtd[unitId] = 0;
        qtd[unitId] += productState.quantity; 
      }

      var units = Object.keys(qtd);
      units.sort();
      if (units.length === 0) return null;

      var first = true;
      var product = this.products.byId[id];
      units.forEach(unitId => {
        if (!product.price[unitId]) return;
        var quantity = Math.round(qtd[unitId] * 1000)/1000; // Solves floating point rounding errors
        body.push(
          [
            first ? product.name : {text: '+', alignment: 'right'},
            {text: quantity, alignment: 'center'},
            {text: product.price[unitId].unit, alignment: 'center'},
          ]
        );
        first = false;
      })
    });

    return [
      'Compras para '+dateStr,
      {
        layout: 'buyLayout',
        style: 'table',
        table: {
          widths: ['*', 70, 70],
          body: body,
        }
      },
    ];
  }

  buildPeriodTotalTable = (orders) => {
    var minDate = null;
    var maxDate = null;

    orders.forEach(order => {
      var d = order.dateClosed ? new Date(order.dateClosed) : new Date();
      if (!minDate || d < minDate) minDate = d;
      if (!maxDate || d > maxDate) maxDate = d;
    });

    var startDateStr = minDate ? dateFormat(minDate, "dd/mm/yyyy") : "";
    var endDateStr = maxDate ? dateFormat(maxDate, "dd/mm/yyyy") : "";

    var productIds = this.products.ids.slice();
    productIds.sort((a, b) => {
      return ('' + this.products.byId[a].name).localeCompare(this.products.byId[b].name);
    });

    var body = [
      [
        { text: 'Produto', style: 'tableHeader' },
        { text: 'Qtd Total', style: 'tableHeader', alignment: 'center' },
        { text: 'Unidade', style: 'tableHeader', alignment: 'center' },
      ],
    ];

    productIds.forEach(id => {
      var qtd = {};

      orders.forEach(order => {
        for (var clientId in order.byClient) {
          var productState = order.byClient[clientId][id];
          if (!productState) continue;

          var unitId = productState.unitId
          if (!qtd[unitId])
            qtd[unitId] = 0;
          qtd[unitId] += productState.quantity;
        }
      });

      var units = Object.keys(qtd);
      units.sort();
      if (units.length === 0) return null;

      var first = true;
      var product = this.products.byId[id];
      units.forEach(unitId => {
        if (!product.price[unitId]) return;
        var quantity = Math.round(qtd[unitId] * 1000) / 1000;
        body.push(
          [
            first ? product.name : { text: '+', alignment: 'right' },
            { text: quantity, alignment: 'center' },
            { text: product.price[unitId].unit, alignment: 'center' },
          ]
        );
        first = false;
      })
    });

    return [
      'Total de Compras: ' + startDateStr + ' a ' + endDateStr,
      {
        layout: 'buyLayout',
        style: 'table',
        table: {
          widths: ['*', 70, 70],
          body: body,
        }
      },
    ];
  }

  buildObservationsTable = (str) => {
    if (str === null || str === "")
      return null;

    return [
      ' ',
      {
        style: 'table',
        table: {
          widths: ['*'],
          body: [
            [
              { text: 'Observações' },
            ],
            [
              { text: str },
            ],
          ],
        },
      }
    ];
  }

  buildSignTable = () => {
    return [
      ' ',
      {
        style: 'table',
        layout: 'noBorders',
        table: {
          widths: [10, '*', '20%', '*', 10],
          body: [
            [
              {},
              { text: '_________________________________', alignment: 'center' },
              {},
              { text: '_________________________________', alignment: 'center' },
              {},
            ],
            [
              {},
              { text: 'Assinatura do Vendedor', alignment: 'center' },
              {},
              { text: 'Assinatura do Cliente', alignment: 'center' },
              {},
            ],
          ],
        },
      }
    ];
  }

  createPdf = (doc) => {
    const { vfs } = vfsFonts.pdfMake;
    pdfMake.vfs = vfs;

    pdfMake.tableLayouts = {
      noBorders: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
      },
      orderLayout: {
        fillColor: function (row, node, col) {
          return row > 0 &&
            row < node.table.body.length - 1 &&
            row % 2 === 1 ? '#DCDCDC' : null;
        }
      },
      orderLayoutWithSubtotal: {
        fillColor: function (row, node, col) {
          return row > 0 &&
            row < node.table.body.length - 3 &&
            row % 2 === 1 ? '#DCDCDC' : null;
        }
      },
      buyLayout: {
        fillColor: function (row, node, col) {
          return row > 0 &&
            row % 2 === 1 ? '#DCDCDC' : null;
        },
      },
    };

    return pdfMake.createPdf(doc);
  }

  getStyles = () => {
    return {
      header: {
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 5]
      },
      subheader: {
        fontSize: 16,
        bold: true,
        margin: [0, 10, 0, 2]
      },
      table: {
        fontSize: 10,
        margin: [0, 5, 0, 10]
      },
      tableClient: {
        fontSize: 10,
        margin: [0, 5, 0, 3]
      },
      tableTotal: {
        bold: true,
      },
      tableHeader: {
        bold: true,
        fontSize: 12,
        color: 'black'
      },
      tableProduct: {
        bold: true,
        fontSize: 10,
      },
      tableSubHeader: {
        fontSize: 12,
      },
    };
  }

}

// Default Export
export default Report;


// WEBPACK FOOTER //
// ./src/data/Report.js